import { PlusCircleFilled } from "@ant-design/icons";
import { ProcessingLineItemInfo, ProcessingLineType, StockDetail } from "@api/tryall/ProcessingApplicationApi";
import DashBoard from "@component/Common/DashBoard";
import PopupAddAndUpdateModal from "@component/Common/PopupAddAndUpdateModal";
import IntegratedTable from "@component/Table/IntegratedTable";
import { storageTypeOptions } from "@constant/StorageType";
import useDebounce from "@hooks/useDebounce";
import {
  addProcessingLineItem,
  deleteProcessingLineItem,
  fetchProcessingLineList,
  fetchProductStockDetail,
  ProcessingApplicationState,
  resetProductStockDetail,
  updateProcessingLineItem,
} from "@redux/ProcessingApplicationSlice";
import tryallPackageSetSkuHandle from "@utils/Tryall/tryallPackageSetSkuHandle";
import { Button, Col, Form, Input, InputNumber, message, Row, Select } from "antd";
import { SelectValue } from "antd/lib/select";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "src/store";
import { FlexBox, TabContentWrapper } from "src/styles/common";
import styled from "styled-components";
import { ProcessingLinePurpose, ProcessingStatus } from "./ProcessingStatus";

const BlockDiv = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

type ContentProps = {
  setShowAlertModal: React.Dispatch<React.SetStateAction<boolean>>;
  purpose: ProcessingLinePurpose;
};

const ProcessingLineTypeLimit = {
  [ProcessingLinePurpose.DE_KITTING]: { target: 999, source: 1 }, // 加工拆包 限制來源為1種
  [ProcessingLinePurpose.KITTING]: { target: 1, source: 999 }, // 加工組合 限制目標為1種
};

const ERROR_MSG = {
  KITTING_MISMATCH: "來源品項內容與數量不符，請確認後再試一次",
  SKU_FORMAT_ERROR: "來源品項的商品品號格式錯誤",
  PRODUCT_NOT_FOUND: "查無對應商品，請確認商品品號碼是否正確",
  QTY_EXCEEDED: "應出數量不得大於庫存數",
};

// 計算目標品項應出數量
const getExpectedProcessingQty = (
  purpose: ProcessingLinePurpose,
  sourceList: ProcessingLineItemInfo[],
  targetNo: string,
): number | undefined => {
  try {
    // 情境 1：SET 加工組合
    if (purpose === ProcessingLinePurpose.KITTING && targetNo?.startsWith("SET")) {
      const [qty, sku] = tryallPackageSetSkuHandle(targetNo);
      if (!qty) return undefined;
      const matchingItems = sourceList.filter((item) => item.no === sku);
      if (matchingItems.length === 0) return undefined;
      const sum = matchingItems.reduce((acc, cur) => acc + cur.qty, 0);
      return sum / qty;
    }

    // 情境 2：SET 加工拆包
    if (purpose === ProcessingLinePurpose.DE_KITTING && sourceList[0]?.no?.startsWith("SET")) {
      const [qty] = tryallPackageSetSkuHandle(sourceList[0].no);
      return qty * sourceList[0].qty;
    }
    // 其他 非SET 情境
    return undefined;
  } catch {
    return undefined;
  }
};

// 驗證來源與目標的品項與數量是否吻合
const isProcessingBalanceValid = (
  purpose: ProcessingLinePurpose,
  sourceList: ProcessingLineItemInfo[],
  targetItem?: { no: string; qty: number },
): boolean => {
  if (!targetItem || sourceList.length === 0) return true;

  try {
    // 情境 1：SET 加工組合
    if (purpose === ProcessingLinePurpose.KITTING && targetItem.no?.startsWith("SET")) {
      const [qty, sku] = tryallPackageSetSkuHandle(targetItem.no);
      if (!qty) return false;
      const matchingItems = sourceList.filter((item) => item.no === sku);
      if (matchingItems.length === 0) return true;
      const sum = matchingItems.reduce((acc, cur) => acc + cur.qty, 0);
      return sum % qty === 0 && targetItem.qty === sum / qty;
    }

    // 情境 2：SET 加工拆包
    if (purpose === ProcessingLinePurpose.DE_KITTING && sourceList[0]?.no?.startsWith("SET")) {
      const expectedQty = getExpectedProcessingQty(purpose, sourceList, targetItem.no);
      return targetItem.no === tryallPackageSetSkuHandle(sourceList[0].no)[1] && targetItem.qty === expectedQty;
    }

    // 非 SET 的組合/拆包：來源與目標品號一樣且數量不一樣不給過
    const mismatch = sourceList.find((item) => item.no === targetItem.no && item.qty !== targetItem.qty);
    return !mismatch;
  } catch {
    return false;
  }
};

const ProcessingApplicationContent = ({ setShowAlertModal, purpose }: ContentProps) => {
  const { details } = useSelector(ProcessingApplicationState);
  const dispatch = useDispatch();
  const { id: applicationIdStr } = useParams();
  const applicationId = Number(applicationIdStr);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editData, setEditData] = useState<ProcessingLineItemInfo | undefined>(undefined);
  const { isLineListFetching, processingLineList } = useSelector(ProcessingApplicationState);
  const { [ProcessingLineType.TARGET]: targetList, [ProcessingLineType.SOURCE]: sourceList } = processingLineList;
  const isPackageProduct = purpose === ProcessingLinePurpose.DE_KITTING && sourceList[0]?.no.startsWith("SET"); // 拆包且來源商品SKU為SET開頭
  const LineLimit = purpose ? ProcessingLineTypeLimit[purpose] : ProcessingLineTypeLimit[ProcessingLinePurpose.KITTING]; // 因為有舊資料所以預設為加工組合

  const accTotalMethod = (data: ProcessingLineItemInfo[]) => {
    return data.reduce(
      (acc, cur) => {
        const { cost, qty } = cur;
        acc.cost += cost * qty;
        acc.qty += qty;
        return acc;
      },
      {
        cost: 0,
        qty: 0,
      },
    );
  };

  const dashBoardData = (listType: ProcessingLineType) => {
    const dataList = listType === ProcessingLineType.SOURCE ? sourceList : targetList;
    const totalObj = accTotalMethod(dataList);

    const returnDataArr = [{ name: "總成本", value: totalObj.cost }];

    if (
      (purpose === ProcessingLinePurpose.KITTING && listType === ProcessingLineType.SOURCE) ||
      (purpose === ProcessingLinePurpose.DE_KITTING && listType === ProcessingLineType.TARGET)
    ) {
      returnDataArr.push({ name: "總量", value: totalObj.qty });
    }

    return returnDataArr;
  };

  const showModal = (type: ProcessingLineType, editMode: boolean = false) => {
    if (type === ProcessingLineType.TARGET) {
      handleShowTargetModal(editMode);
      return;
    }
    handleShowSourceModal(editMode);
  };

  const handleShowTargetModal = (editMode: boolean) => {
    if (editMode) {
      setShowTargetModal(true);
      return;
    }
    if (sourceList.length === 0) {
      message.error("請先新增來源品項");
      return;
    }
    if (isPackageProduct && targetList.length > 0) {
      message.error("組合拆包時，目標品項只能有一筆");
      return;
    }
    if (LineLimit && targetList.length >= LineLimit.target) {
      message.error(`目標品項數量已達上限(${LineLimit.target})`);
      return;
    }

    setShowTargetModal(true);
  };
  const handleShowSourceModal = (editMode: boolean) => {
    if (editMode) {
      setShowSourceModal(true);
      return;
    }
    if (LineLimit && sourceList.length >= LineLimit.source) {
      message.error(`來源品項數量已達上限(${LineLimit.source})`);
      return;
    }
    setShowSourceModal(true);
  };

  const closeModal = () => {
    setShowTargetModal(false);
    setShowSourceModal(false);
    setEditData(undefined);
  };

  const handleDelete = (processingAppId: number, type: ProcessingLineType, itemId: number) => {
    dispatch(deleteProcessingLineItem({ processingAppId, itemId }));
  };

  const tableColumns = (type: ProcessingLineType): ColumnsType<any> => {
    return [
      {
        key: "no",
        dataIndex: "no",
        title: "商品品號",
        width: "10%",
      },
      {
        key: "name",
        dataIndex: "name",
        title: "商品名稱",
        width: "20%",
      },
      {
        key: "storageType",
        dataIndex: "storageType",
        title: "倉別",
        width: 100,
        align: "center",
        render: (value) => {
          return storageTypeOptions.find((option) => option.value === value)?.name || value;
        },
      },
      {
        key: "",
        dataIndex: "",
        title: "效期_批號",
        width: "20%",
        align: "center",
        render: (value, record) => {
          return `${record.effectiveDate || ""}_${record.batch || ""}`;
        },
      },
      {
        key: "cost",
        dataIndex: "cost",
        title: "成本",
        width: 120,
        align: "center",
      },
      {
        key: "qty",
        dataIndex: "qty",
        title: "應出數量",
        width: 120,
        align: "center",
      },
      {
        key: "arrivedQty",
        dataIndex: "arrivedQty",
        title: "實際加工數量",
        width: 200,
        align: "center",
      },
      {
        key: "",
        dataIndex: "",
        title: "",
        render: (_: any, record) => (
          <FlexBox>
            <Button
              type="link"
              onClick={() => {
                showModal(type, true);
                setEditData(record);
              }}
              disabled={details?.status !== ProcessingStatus.PENDING}
            >
              編輯
            </Button>
            <Button
              type="link"
              onClick={() => {
                if (record.id) handleDelete(applicationId, type, record.id);
              }}
              disabled={details?.status !== ProcessingStatus.PENDING}
            >
              刪除
            </Button>
          </FlexBox>
        ),
      },
    ];
  };

  useEffect(() => {
    dispatch(fetchProcessingLineList(applicationId));
  }, [applicationId, dispatch]);

  useEffect(() => {
    setShowAlertModal(!isProcessingBalanceValid(purpose, sourceList, targetList[0]));
  }, [sourceList, targetList, purpose, setShowAlertModal]);

  return (
    <TabContentWrapper>
      {showTargetModal && (
        <TargetLineItemAddModal
          onClose={closeModal}
          editData={editData}
          sourceProduct={isPackageProduct ? sourceList[0] : undefined}
          sourceList={sourceList}
          purpose={purpose}
        />
      )}
      {showSourceModal && (
        <SourceLineItemAddModal onClose={closeModal} editData={editData} sourceList={sourceList} purpose={purpose} />
      )}
      <BlockDiv>
        <Row align="middle" gutter={[24, 0]}>
          <Col>
            <Row gutter={[0, 8]}>
              <Col>來源品項</Col>
            </Row>
            <Row gutter={[0, 8]}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusCircleFilled />}
                  onClick={() => showModal(ProcessingLineType.SOURCE)}
                  disabled={details?.status !== ProcessingStatus.PENDING || sourceList.length >= LineLimit?.source}
                >
                  新增
                </Button>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <DashBoard dataset={dashBoardData(ProcessingLineType.SOURCE)} isFetching={isLineListFetching} />
          </Col>
        </Row>
        <IntegratedTable
          isPagination={false}
          dataSource={sourceList}
          columns={tableColumns(ProcessingLineType.SOURCE)}
          scroll={{ x: 1200, y: 300 }}
          tableLayout="auto"
          loading={isLineListFetching}
          rowKey="name"
        />
      </BlockDiv>
      <BlockDiv>
        <Row align="middle" gutter={[24, 0]}>
          <Col>
            <Row gutter={[0, 8]}>
              <Col>目標品項</Col>
            </Row>
            <Row gutter={[0, 8]}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusCircleFilled />}
                  onClick={() => {
                    showModal(ProcessingLineType.TARGET);
                  }}
                  disabled={details?.status !== ProcessingStatus.PENDING || targetList.length >= LineLimit.target}
                >
                  新增
                </Button>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <DashBoard dataset={dashBoardData(ProcessingLineType.TARGET)} isFetching={isLineListFetching} />
          </Col>
        </Row>
        <IntegratedTable
          isPagination={false}
          dataSource={targetList}
          columns={tableColumns(ProcessingLineType.TARGET)}
          scroll={{ x: 1200, y: 500 }}
          tableLayout="auto"
          loading={isLineListFetching}
          rowKey="name"
        />
      </BlockDiv>
    </TabContentWrapper>
  );
};

export default ProcessingApplicationContent;

type ModalProps = {
  onClose: () => void;
  editData?: ProcessingLineItemInfo;
  sourceProduct?: ProcessingLineItemInfo;
  sourceList: ProcessingLineItemInfo[];
  purpose: ProcessingLinePurpose;
};

// 目標品項 Modal
const TargetLineItemAddModal: FC<ModalProps> = (props) => {
  const { onClose, editData, sourceProduct, sourceList, purpose } = props;
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { id: applicationIdStr } = useParams();
  const applicationId = Number(applicationIdStr);
  const { isFetching, productStockDetail, isNoStockChecked } = useSelector(ProcessingApplicationState);

  const noCheckState = useMemo(() => {
    if (isFetching) return "validating";
    return isNoStockChecked ? "error" : undefined;
  }, [isFetching, isNoStockChecked]);

  const fetchNoInfo = useDebounce((value: string) => {
    if (value) {
      dispatch(fetchProductStockDetail(value));
    } else {
      dispatch(resetProductStockDetail());
      form.resetFields();
    }
  }, 1000);

  const handleOnAdd = () => {
    const values = form.getFieldsValue();
    const { cost, name, effectiveDate, ...restData } = values;
    dispatch(
      addProcessingLineItem({
        processingAppId: applicationId,
        type: ProcessingLineType.TARGET,
        info: {
          ...restData,
          effectiveDate: effectiveDate ? moment(effectiveDate).format("YYYY-MM-DD") : undefined,
        },
      }),
    );
  };

  const handleOnUpdate = () => {
    if (editData) {
      const values = form.getFieldsValue();
      const { cost, name, effectiveDate, ...restData } = values;

      dispatch(
        updateProcessingLineItem({
          processingAppId: applicationId,
          id: editData.id,
          type: ProcessingLineType.TARGET,
          info: {
            ...restData,
            effectiveDate: effectiveDate ? moment(effectiveDate).format("YYYY-MM-DD") : undefined,
          },
        }),
      );
    }
  };

  const resetFormValues = () => {
    dispatch(resetProductStockDetail());
    form.resetFields();
  };

  const handleOnclose = () => {
    resetFormValues();
    onClose();
  };

  const qtyValidator = async (_: any, value: number) => {
    if (value === undefined || value === null) return Promise.resolve();

    const no = form.getFieldValue("no");
    const valid = isProcessingBalanceValid(purpose, sourceList, { no, qty: value });

    return valid ? Promise.resolve() : Promise.reject(new Error(ERROR_MSG.KITTING_MISMATCH));
  };

  // 加工緣由為拆包時,來源品項為SET開頭的品號,則目標品項的商品品號和數量自動帶入且disabled
  useEffect(() => {
    if (!sourceProduct) return;

    try {
      const [qty, sku] = tryallPackageSetSkuHandle(sourceProduct.no);
      dispatch(fetchProductStockDetail(sku));
      form.setFieldsValue({
        no: sku,
        qty: qty * sourceProduct.qty,
      });
    } catch (e) {
      message.error("來源品項的商品品號格式錯誤");
    }
  }, [sourceProduct, form, dispatch]);

  // 加工組合 & 目標品項為 SET： 由來源品項計算應出數量
  useEffect(() => {
    const no = form.getFieldValue("no");
    if (
      purpose === ProcessingLinePurpose.KITTING &&
      no?.startsWith("SET") &&
      productStockDetail &&
      sourceList.length > 0
    ) {
      const expectedQty = getExpectedProcessingQty(purpose, sourceList, no);
      if (expectedQty !== undefined) {
        form.setFieldsValue({ qty: expectedQty });
        form.validateFields(["qty"]);
      }
    }
  }, [purpose, productStockDetail, sourceList, form]);

  useEffect(() => {
    // 編輯資料處理
    if (editData) {
      const formData = {
        ...editData,
        storageType:
          storageTypeOptions.find((option) => option.value === editData.storageType)?.value || editData.storageType,
      };
      form.setFieldsValue(formData);
      dispatch(fetchProductStockDetail(editData.no));
    }
  }, [form, editData, dispatch]);

  useEffect(() => {
    // fetch商品品號資料處理
    if (productStockDetail) {
      form.setFieldsValue({
        name: productStockDetail.name,
        cost: productStockDetail.cost,
      });
    }
  }, [form, productStockDetail]);

  return (
    <PopupAddAndUpdateModal
      form={form}
      onClose={handleOnclose}
      onAdd={handleOnAdd}
      onUpdate={handleOnUpdate}
      initialValues={editData ? form.getFieldsValue() : undefined}
      width={520}
      padding={20}
      AddText="建立"
      updateText="確定"
      title="目標品項"
    >
      <>
        <Form.Item
          label="商品品號"
          name="no"
          labelCol={{ span: 4 }}
          hasFeedback
          validateStatus={noCheckState}
          help={isNoStockChecked || undefined}
          rules={[{ required: true, message: "請輸入商品品號" }]}
        >
          <Input
            allowClear
            onChange={(e) => {
              fetchNoInfo(e.target.value);
            }}
            disabled={!!sourceProduct}
          />
        </Form.Item>
        <Form.Item
          label="商品名稱"
          name="name"
          labelCol={{ span: 4 }}
          rules={[
            {
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(ERROR_MSG.PRODUCT_NOT_FOUND));
              },
            },
          ]}
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="倉別"
          name="storageType"
          labelCol={{ span: 4 }}
          rules={[{ required: true, message: "請選擇倉別" }]}
        >
          <Select placeholder="請選擇">
            {storageTypeOptions?.map((option) => (
              <Select.Option key={option.name} value={option.value}>
                {option.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="批號" name="batch" labelCol={{ span: 4 }}>
          <Input allowClear />
        </Form.Item>
        <Form.Item label="成本" name="cost" labelCol={{ span: 4 }}>
          <Input disabled />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.no !== curr?.no}>
          {({ getFieldValue }) => {
            return (
              <Form.Item
                label="應出數量"
                name="qty"
                labelCol={{ span: 4 }}
                rules={[{ required: true, message: "請輸入應出數量" }, { validator: qtyValidator }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  disabled={
                    !!sourceProduct ||
                    (purpose === ProcessingLinePurpose.KITTING && form.getFieldValue("no")?.startsWith("SET"))
                  }
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </>
    </PopupAddAndUpdateModal>
  );
};

// 來源品項 Modal
const SourceLineItemAddModal: FC<ModalProps> = (props) => {
  const { onClose, editData } = props;
  const dispatch = useAppDispatch();
  const { id: applicationIdStr } = useParams();
  const applicationId = Number(applicationIdStr);
  const [form] = Form.useForm();
  const { isFetching, productStockDetail, isNoStockChecked } = useSelector(ProcessingApplicationState);
  const [storageTypeOpts, setStorageTypeOptions] = useState<typeof storageTypeOptions>();
  const [isEditInit, setIsEditInit] = useState<boolean>(true);

  const noCheckState = useMemo(() => {
    if (isFetching) return "validating";
    return isNoStockChecked ? "error" : undefined;
  }, [isFetching, isNoStockChecked]);

  const fetchNoInfo = useDebounce((value: string) => {
    if (value) {
      dispatch(fetchProductStockDetail(value));
    } else {
      dispatch(resetProductStockDetail());
      form.resetFields();
    }
  }, 1000);

  const handelEffectiveDateBatch = (values: SelectValue) => {
    const [effectiveDate, batch] = JSON.parse(values as string);
    const storeType = form.getFieldValue("storageType");
    const effectiveDateBatchOptions = productStockDetail?.stocks[storeType];
    const target = effectiveDateBatchOptions?.find(
      (option: StockDetail) => option.effectiveDate === effectiveDate && option.batch === batch,
    );
    form.setFieldsValue({
      effectiveDate: moment(effectiveDate),
      batch,
      canSalesQty: target?.canSalesQty,
    });
  };

  const handleOnAdd = () => {
    const values = form.getFieldsValue();
    const { name, cost, canSalesQty, effectiveDateBatch, ...resetValues } = values;
    const [effectiveDate, batch] = JSON.parse(effectiveDateBatch as string);
    dispatch(
      addProcessingLineItem({
        processingAppId: applicationId,
        type: ProcessingLineType.SOURCE,
        info: {
          ...resetValues,
          effectiveDate,
          batch,
        },
      }),
    );
  };

  const handleOnUpdate = () => {
    if (editData) {
      const values = form.getFieldsValue();
      const { name, cost, canSalesQty, effectiveDateBatch, ...resetValues } = values;
      const [effectiveDate, batch] = JSON.parse(effectiveDateBatch as string);
      dispatch(
        updateProcessingLineItem({
          processingAppId: applicationId,
          id: editData.id,
          type: ProcessingLineType.SOURCE,
          info: {
            ...resetValues,
            effectiveDate,
            batch,
          },
        }),
      );
    }
  };
  const resetFormValues = () => {
    dispatch(resetProductStockDetail());
    form.resetFields();
  };

  const handleOnclose = () => {
    resetFormValues();
    onClose();
  };

  const qtyValidator = async (_: any, value: number) => {
    const canSalesQty = form.getFieldValue("canSalesQty");
    if (canSalesQty !== null && value > canSalesQty) {
      return Promise.reject(new Error(ERROR_MSG.QTY_EXCEEDED));
    }
    return Promise.resolve();
  };

  useEffect(() => {
    // 編輯資料處理 -> 取得商品品號資料
    if (editData && isEditInit) {
      dispatch(fetchProductStockDetail(editData.no));
    }
  }, [form, editData, dispatch, isEditInit]);

  useEffect(() => {
    // fetch商品品號資料處理：1. 初始編輯資料處理 2. 輸入商品品號，fetch品號資料
    if (!productStockDetail) return;

    //  生成倉別選項
    const { stocks } = productStockDetail;
    if (stocks) {
      const stockKeys = Object.keys(stocks);
      const storeTypeOptions = storageTypeOptions.filter((option) => stockKeys.includes(option.key));
      setStorageTypeOptions(storeTypeOptions);
    }

    // 2. 輸入商品品號，fetch品號資料處理 -> 新增(!editData) or 非初始編輯資料處理(!isEditInit)
    if (!editData || !isEditInit) {
      form.setFieldsValue({
        name: productStockDetail.name,
        cost: productStockDetail.cost,
        storageType: undefined,
        effectiveDateBatch: undefined,
        canSalesQty: undefined,
      });
      return;
    }

    // 1. 初始編輯資料處理 ->
    if (editData && isEditInit) {
      const { storageType, effectiveDate, batch } = editData;
      const selectedStorageType = productStockDetail.stocks[storageType]?.find(
        (stock) => stock.effectiveDate === effectiveDate && stock.batch === batch,
      );
      const effectiveDateBatch = selectedStorageType ? JSON.stringify([effectiveDate, batch]) : undefined;
      const formData = {
        ...editData,
        storageType: storageTypeOptions.find((option) => option.value === editData.storageType)?.value || undefined,
        effectiveDateBatch,
        canSalesQty: selectedStorageType?.canSalesQty || undefined,
      };
      form.setFieldsValue(formData);
      setIsEditInit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, form, productStockDetail]);

  return (
    <PopupAddAndUpdateModal
      form={form}
      onClose={handleOnclose}
      onAdd={handleOnAdd}
      onUpdate={handleOnUpdate}
      initialValues={editData ? form.getFieldsValue() : undefined}
      width={520}
      padding={20}
      AddText="建立"
      updateText="確定"
      title="來源品項"
    >
      <>
        <Form.Item
          label="商品品號"
          name="no"
          labelCol={{ span: 5 }}
          rules={[{ required: true, message: "請輸入商品品號" }]}
          hasFeedback
          validateStatus={noCheckState}
          help={isNoStockChecked || undefined}
        >
          <Input
            onChange={(e) => {
              fetchNoInfo(e.target.value);
            }}
          />
        </Form.Item>
        <Form.Item
          label="商品名稱"
          name="name"
          labelCol={{ span: 5 }}
          rules={[
            {
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(ERROR_MSG.PRODUCT_NOT_FOUND));
              },
            },
          ]}
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="倉別"
          name="storageType"
          labelCol={{ span: 5 }}
          rules={[{ required: true, message: "請選擇倉別" }]}
        >
          <Select
            placeholder="請選擇"
            onChange={(e) => {
              form.setFieldsValue({ effectiveDateBatch: undefined, canSalesQty: undefined });
            }}
          >
            {storageTypeOpts?.map((option) => (
              <Select.Option key={option.name} value={option.value}>
                {option.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const storeType = getFieldValue("storageType");
            const effectiveDateBatchOptions = productStockDetail?.stocks[storeType];
            return (
              <Form.Item
                name="effectiveDateBatch"
                label="效期_批號"
                labelCol={{ span: 5 }}
                rules={[{ required: true, message: "請選擇效期_批號" }]}
                shouldUpdate
              >
                <Select placeholder="請選擇" onSelect={handelEffectiveDateBatch}>
                  {effectiveDateBatchOptions?.map((option: StockDetail) => {
                    const { effectiveDate = "", batch = "" } = option;
                    const value = JSON.stringify([effectiveDate, batch]);
                    const effectiveDateBatch = `${effectiveDate}_${batch}`;
                    return (
                      <Select.Option key={effectiveDateBatch} value={value}>
                        {effectiveDateBatch}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item label="成本" name="cost" labelCol={{ span: 5 }}>
          <Input disabled />
        </Form.Item>
        <Form.Item label="庫存數" name="canSalesQty" labelCol={{ span: 5 }}>
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="應出數量"
          name="qty"
          labelCol={{ span: 5 }}
          rules={[{ required: true, message: "請輸入應出數量" }, { validator: qtyValidator }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
      </>
    </PopupAddAndUpdateModal>
  );
};
